/**
 * Markup for the three multiplayer panels: the lobby, the connecting screen and
 * the disconnect screen.
 *
 * Pure string functions, like ./training's panels, so what the lobby says about
 * a roster is testable in Node — and so ./menu.ts stays a file that lays things
 * out rather than one that decides them.
 *
 * The visual language is ./style.ts's throughout: uppercase tracked labels, one
 * type scale, `tabular-nums` on every figure, hard right-aligned value columns.
 * A ping column that shuffles horizontally as the numbers change is the exact
 * thing `font-variant-numeric` exists to prevent, and it matters most here,
 * where four of them update once a second.
 */

import {
  humansOn,
  netStatusLine,
  pingBand,
  pingLabel,
  sortedRoster,
  type NetView,
} from './net-model';

const TEAM_NAMES = ['Blue', 'Orange'] as const;
/** Matches TEAM_COLORS in src/render/materials.ts; text, not an import, so this
 *  module stays free of the Three.js layer. */
const TEAM_HEX = ['#2a7dff', '#ff8a1f'] as const;

const esc = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// ---------------------------------------------------------------------------
// Lobby
// ---------------------------------------------------------------------------

/**
 * The join code, set big.
 *
 * The single most-used thing on this screen: it is read aloud over voice chat
 * and typed into somebody else's browser, so it is spaced out, upper case and
 * large enough to read from across a desk. Rendered as individual characters
 * rather than as letter-spaced text because a trailing letter-space shifts an
 * otherwise centred string half a character off centre, which looks like a bug
 * on the one element the whole screen is about.
 */
export const joinCodeMarkup = (code: string | null): string => {
  if (code === null || code.length === 0) {
    return (
      `<div class="rw-code empty"><div class="rw-code-key">Join Code</div>` +
      `<div class="rw-code-none">Matchmade rooms have no code</div></div>`
    );
  }
  const chars = [...code].map(c => `<b>${esc(c)}</b>`).join('');
  return (
    `<div class="rw-code"><div class="rw-code-key">Join Code</div>` +
    `<div class="rw-code-value">${chars}</div></div>`
  );
};

/** One roster line: name, team, car, ready, ping. */
const playerRowMarkup = (
  player: ReturnType<typeof sortedRoster>[number],
  teams: boolean
): string => {
  const classes = ['rw-lp'];
  if (player.isLocal) classes.push('me');
  if (player.isBot) classes.push('bot');

  const tag = player.isBot
    ? '<i>BOT</i>'
    : player.carIndex === null
      ? '<i>SPECTATOR</i>'
      : player.isLocal
        ? '<i>YOU</i>'
        : '';
  const team = teams
    ? `<div class="rw-lp-team" style="color:${TEAM_HEX[player.team]}">${TEAM_NAMES[player.team]}</div>`
    : `<div class="rw-lp-team">—</div>`;
  // A bot is always "ready" in the room's arithmetic, which is true and would
  // read as a bot that pressed a button. It gets a dash instead.
  const ready = player.isBot
    ? `<div class="rw-lp-ready">—</div>`
    : `<div class="rw-lp-ready${player.ready ? ' on' : ''}">${player.ready ? 'Ready' : 'Waiting'}</div>`;
  const ping = player.isBot
    ? `<div class="rw-lp-ping">—</div>`
    : `<div class="rw-lp-ping" data-band="${pingBand(player.ping)}">${pingLabel(player.ping)}</div>`;

  return (
    `<div class="${classes.join(' ')}">` +
    `<div class="rw-lp-name">${esc(player.name)}${tag}</div>` +
    team +
    `<div class="rw-lp-car">${esc(player.car)}</div>` +
    ready +
    ping +
    `</div>`
  );
};

export const lobbyPanelMarkup = (view: NetView): string => {
  const rows = sortedRoster(view.players);
  const list =
    rows.length === 0
      ? `<div class="rw-lp empty"><div class="rw-lp-name">Waiting for the roster…</div></div>`
      : rows.map(player => playerRowMarkup(player, view.teams)).join('');

  const counts = view.teams
    ? `<div class="rw-lobby-counts">` +
      `<span style="color:${TEAM_HEX[0]}">Blue ${humansOn(view.players, 0)}/${view.teamSize}</span>` +
      `<span style="color:${TEAM_HEX[1]}">Orange ${humansOn(view.players, 1)}/${view.teamSize}</span>` +
      `</div>`
    : `<div class="rw-lobby-counts"><span>${view.players.filter(p => !p.isBot).length} here</span></div>`;

  return (
    `<div class="rw-lobby">` +
    joinCodeMarkup(view.code) +
    counts +
    `<div class="rw-lp head">` +
    `<div class="rw-lp-name">Player</div><div class="rw-lp-team">Team</div>` +
    `<div class="rw-lp-car">Car</div><div class="rw-lp-ready">Status</div>` +
    `<div class="rw-lp-ping">Ping</div></div>` +
    list +
    `</div>`
  );
};

// ---------------------------------------------------------------------------
// Connecting
// ---------------------------------------------------------------------------

/**
 * The connecting screen's panel.
 *
 * Names the address being dialled, because "connecting" with no host on screen
 * is unactionable the moment it fails — and the most common cause of a failure
 * here is an address with the wrong port in it, which the player cannot see
 * unless it is written down.
 */
export const connectingPanelMarkup = (view: NetView, address: string): string => {
  const queue = view.queue;
  const bar =
    queue !== null && queue.needed > 0
      ? `<div class="rw-q"><i style="width:${Math.min(100, (queue.waiting / queue.needed) * 100).toFixed(1)}%"></i></div>`
      : `<div class="rw-q indeterminate"><i></i></div>`;

  return (
    `<div class="rw-connect">` +
    `<div class="rw-connect-line">${esc(netStatusLine(view))}</div>` +
    bar +
    `<div class="rw-connect-host"><span>Server</span><b>${esc(address)}</b></div>` +
    (view.retry > 0
      ? `<div class="rw-connect-host"><span>Attempt</span><b>${view.retry} of ${view.retryLimit}</b></div>`
      : '') +
    `</div>`
  );
};

// ---------------------------------------------------------------------------
// Disconnected
// ---------------------------------------------------------------------------

/**
 * What went wrong, in one sentence, plus the room it happened in.
 *
 * `view.message` is already a full sentence from `failureMessage` in
 * src/net/client.ts — one definition of what each failure means, so the screen
 * and the log cannot describe the same drop differently.
 */
export const disconnectPanelMarkup = (view: NetView): string =>
  `<div class="rw-dc">` +
  `<div class="rw-dc-code">${esc(view.failure ?? 'disconnected')}</div>` +
  `<div class="rw-dc-text">${esc(view.message)}</div>` +
  (view.code !== null ? `<div class="rw-dc-room">Room ${esc(view.code)}</div>` : '') +
  `</div>`;
